<template>
  <TemplateBase :page="page">
    <!-- Slot with back button with flush false so it is positioned to the header -->
    <template #backButton>
      <MoleculeButtonBreadcrumbBack :breadcrumbs="breadcrumbs" />
    </template>

    <div class="grid grid-cols-12 bg-white sm:bg-transparent">
      <section v-if="page.image" class="col-span-full row-start-1 bg-white sm:bg-transparent z-0 v-margin-page-image-to-page-content">
        <MoleculeMaskVisual :image="page.image" />
      </section>

      <OrganismPageSectionContent
        class="row-start-2 col-span-full"
        :at-page-top="!page.image"
        :flat-bottom="true"
        :title="page.title"
        :introduction="page.introduction"
        :description="page.description"
        prose-class="prose--app--text-introduction-size"
      >
        <template #chapeau>
          <div
            v-if="get(page, 'organizer.title')"
            class="border-solid border-b md:border-b-2 border-app-primary-grey--black-squeeze pb-6 mb-6 flex flex-wrap items-start justify-between u-width--mx80ch"
          >
            <!-- 2 columns on mobile when there is no organiser image -->
            <ul
              class="grid text-xs sm:text-sm gap-y-4 sm:gap-x-8"
              :class="{ 'flex-1 grid-cols-1 sm:grid-cols-2 mr-4 sm:mr-8': page.organizer.image, 'grid-cols-2 gap-x-4': !page.organizer.image }"
            >
              <li class="flex-auto">
                <h4 class="font-semibold sm:mb-1">{{ $t('series-detail-organizer-label') }}</h4>
                <p>{{ page.organizer.title }}</p>
              </li>
              <li v-if="page.minPrice" class="flex-auto">
                <h4 class="font-semibold sm:mb-1">{{ $t('series-price-title') }}</h4>
                <p>€ {{ page.minPrice }}</p>
              </li>
              <li v-if="page.room && page.room.title" class="flex-auto">
                <h4 class="font-semibold sm:mb-1">{{ $t('series.rooms.title', 1) }}</h4>
                <p>{{ page.room.title }}</p>
              </li>
              <li v-if="page.genres && page.genres.length" class="flex-auto">
                <h4 class="font-semibold sm:mb-1">{{ $t('series.genres.title', page.genres.length) }}</h4>
                <p>{{ page.genres.map((genre: CmsData) => genre.title).join(', ') }}</p>
              </li>
              <li v-if="season" class="flex-auto">
                <h4 class="font-semibold sm:mb-1">{{ t_('season-label') }}</h4>
                <p>{{ season }}</p>
              </li>
              <li v-if="!serieSale.available && serieSale.dateAvailable" class="flex-auto">
                <h4 class="font-semibold sm:mb-1">{{ $t('series-sales-available-from-date') }}</h4>
                <p>{{ $d(serieSale.dateAvailable, 'long-date-time') }}</p>
              </li>
            </ul>
            <MoleculePicture v-if="page.organizer.image" :image="page.organizer.image" :alt="page.organizer.image.title" class="block shrink-0" />
          </div>
        </template>

        <div class="u-width--mx80ch mt-6 lg:mt-8 mb-8 sm:mb-0">
          <div v-if="page.usp">
            <AtomHeadingPageSection class="mb-5" :title="$t('series-detail-usp-header')" />
            <div class="prose prose--app prose--app--usp" :class="{ 'mb-6 lg:mb-8': !page.purchaseUrl, 'mb-4': page.purchaseUrl }" v-html="page.usp" />
          </div>

          <div v-if="serieSale.available" class="inline-flex w-full max-w-xs flex-col items-center mb-8 lg:mb-10 gap-2">
            <MoleculeButton :url="page.purchaseUrl" class="w-full" background-color="bg-app-primary-1-red--fire-brick hover:bg-black">{{
              $t('series-detail-order-button-label')
            }}</MoleculeButton>
            <p v-if="page.discount" class="font-medium inline-flex">{{ t_('discount', { discount: page.discount }) }}</p>
          </div>
          <p v-else-if="!serieSale.available" class="mb-10 font-semibold">{{ t_('order-not-available') }}</p>

          <div v-if="page.events && page.events.length">
            <AtomHeadingPageSection class="mb-5" :title="$t('series-detail-events-header')" />
            <ul class="-mx-4 sm:mx-0">
              <li
                v-for="({ uri }, index) in page.events"
                :key="index"
                class="border-t md:border-t-2 border-solid border-app-primary-grey--black-squeeze"
                :class="{ 'border-b md:border-b-2': Number(index) + 1 === page.events.length }"
              >
                <OrganismLoaderFromUri v-slot="event" :uri="uri">
                  <MoleculeFoldoutItem :when="event.eventDate ? new Date(event.eventDate) : undefined" :label="get(event, 'production.title')">
                    <template #foldoutContent>
                      <MoleculeEventDetailPageInfoData
                        class="mb-5"
                        :event-date="event.eventDate ? new Date(event.eventDate) : undefined"
                        :event-end-date="event.eventEndDate ? new Date(event.eventEndDate) : undefined"
                        :location="get(event, 'room.title')"
                      />
                      <div v-if="event.production.introduction" class="md:text-lg leading-relaxed mb-6" v-html="get(event, 'production.introduction')" />
                      <MoleculeProgram :musicians="get(event, 'production.musicians')" :audio-works="get(event, 'production.works')" :show-intermission="false" />
                    </template>
                  </MoleculeFoldoutItem>
                </OrganismLoaderFromUri>
              </li>
            </ul>
          </div>
        </div>
      </OrganismPageSectionContent>
    </div>
  </TemplateBase>
</template>

<script setup lang="ts">
import get from 'lodash/get'

/*
 * A computed rather than a ref holding the two computeds: the sale state changes after login, and a ref
 * whose properties are themselves refs only reads correctly through Vue's nested-ref unwrapping.
 */
function createSerieSale(page: CmsData, tixSale: ReturnType<typeof setupTixSale>) {
  return computed(() => {
    if (typeof page.purchaseUrl === 'string' && page.purchaseUrl.length > 0) {
      return { available: unref(tixSale.saleIsAvailable), dateAvailable: unref(tixSale.saleAvailableFromDate) }
    }

    return { available: false, dateAvailable: undefined }
  })
}

function extractSeasonFromEvents(events?: CmsData[] | null) {
  if (!Array.isArray(events) || events.length === 0) {
    return null
  }

  const firstEvent = events[0]
  const lastEvent = events[events.length - 1]

  /*
   * Both dates are needed to derive a season. The previous condition only bailed out when *both* were
   * missing, so a series with one dated event and one undated one fell through to `.substring()` on
   * undefined; treat a single missing date as "no season" instead.
   */
  if (!firstEvent?.eventDate || !lastEvent?.eventDate) {
    return null
  }

  const firstEventDate = { year: parseInt(firstEvent.eventDate.substring(0, 4)), month: parseInt(firstEvent.eventDate.substring(5, 7)) }
  const lastEventDate = { year: parseInt(lastEvent.eventDate.substring(0, 4)), month: parseInt(lastEvent.eventDate.substring(5, 7)) }

  const firstEventYear = firstEventDate.month >= 9 ? firstEventDate.year : firstEventDate.year - 1
  const lastEventYear = lastEventDate.year

  return firstEventYear === lastEventYear ? firstEventYear : `${firstEventYear} - ${lastEventYear}`
}

const props = defineProps({
  breadcrumbs: { type: Array as PropType<any[]>, required: true },
  page: { type: Object as PropType<Record<string, any>>, required: true },
})

// setupTixSale internally routes through setupTix(), which uses Pinia stores / useRuntimeConfig / useRoute -
// must be called before any await, there is none in this component.
const tixSale = setupTixSale(props.page)
const season = computed(() => extractSeasonFromEvents(props.page.events))

// todo: move all default translations to translation prefixer
const { t_ } = setupTranslationPrefixer('SeriesDetail')
const serieSale = createSerieSale(props.page, tixSale)
</script>
